import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import {
  IsOptional,
  IsString,
  IsDateString,
  IsArray,
  IsNumber,
  ArrayMinSize,
} from 'class-validator';

/**
 * DTO para información de un documento individual de Control DUS
 */
export class ControlDusFileDto {
  @ApiProperty({
    description: 'ID del documento',
    example: 41750,
  })
  id!: number;

  @ApiProperty({
    description: 'Número de instrucción de embarque (nroIE)',
    example: 6216,
  })
  nroIE!: number;

  @ApiProperty({
    description: 'Nombre del archivo',
    example: '20251015093240639_MEDUFP693098_FC.PDF',
  })
  fileName!: string;

  @ApiProperty({
    description: 'Tipo de documento',
    example: 'B/L',
  })
  fileType!: string;

  @ApiPropertyOptional({
    description: 'Número del documento',
    example: 'MEDUFP693098',
  })
  documentNumber?: string;

  @ApiPropertyOptional({
    description: 'URL de descarga individual',
    example: '/api/v1/control-dus/6216/documents/41750/download',
  })
  downloadUrl?: string;

  @ApiPropertyOptional({
    description: 'Tamaño del archivo formateado',
    example: '1.2 MB',
  })
  fileSize?: string;

  @ApiProperty({
    description: 'Fecha de creación del documento',
    example: '2025-10-15T09:32:40.640Z',
    type: Date,
  })
  creationDate!: Date;

  @ApiPropertyOptional({
    description: 'Extensión del archivo',
    example: 'pdf',
  })
  extension?: string;

  @ApiPropertyOptional({
    description: 'Usuario que creó el documento',
    example: 'matias.gonzalez@opb.cl',
  })
  createdBy?: string;

  @ApiPropertyOptional({
    description: 'Fecha de última actualización',
    example: '2025-10-15T09:32:40.640Z',
    type: Date,
  })
  lastUpdateDate?: Date;

  @ApiPropertyOptional({
    description: 'Usuario que actualizó',
    example: 'matias.gonzalez@opb.cl',
  })
  lastUpdateBy?: string;

  @ApiPropertyOptional({
    description: 'Observaciones/comentarios',
    example: '',
  })
  remarks?: string;
}

/**
 * DTO para la respuesta completa de documentos de Control DUS
 */
export class ControlDusFilesResponseDto {
  @ApiProperty({
    description: 'Número de instrucción de embarque',
    example: 6216,
  })
  nroIE!: number;

  @ApiPropertyOptional({
    description: 'Cliente asociado',
    example: 'MEREX',
  })
  cliente?: string;

  @ApiPropertyOptional({
    description: 'Número de DUS',
    example: '13011604-3',
  })
  dus?: string;

  @ApiProperty({
    description: 'Lista de documentos',
    type: [ControlDusFileDto],
  })
  files!: ControlDusFileDto[];

  @ApiProperty({
    description: 'Opciones de descarga',
  })
  downloadOptions!: {
    zipUrl?: string;
    pdfMergeUrl?: string;
  };

  @ApiProperty({
    description: 'Total de documentos',
    example: 4,
  })
  totalFiles!: number;

  @ApiPropertyOptional({
    description: 'Tamaño total de archivos',
    example: '5.8 MB',
  })
  totalSize?: string;
}

/**
 * DTO para filtros de documentos de Control DUS
 */
export class ControlDusFileFilterDto {
  @ApiPropertyOptional({
    description: 'Filtrar por tipo de documento',
    example: 'B/L',
  })
  @IsOptional()
  @IsString()
  fileType?: string;

  @ApiPropertyOptional({
    description: 'Filtrar por número de contenedor',
    example: 'MSNU8697170',
  })
  @IsOptional()
  @IsString()
  containerNumber?: string;

  @ApiPropertyOptional({
    description: 'Filtrar por extensión',
    example: 'pdf',
  })
  @IsOptional()
  @IsString()
  extension?: string;

  @ApiPropertyOptional({
    description: 'Fecha desde (YYYY-MM-DD)',
    example: '2025-01-01',
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  @ApiPropertyOptional({
    description: 'Fecha hasta (YYYY-MM-DD)',
    example: '2025-12-31',
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;
}

/**
 * DTO para solicitud de merge selectivo de PDFs
 */
export class ControlDusMergeSelectedDto {
  @ApiProperty({
    description: 'IDs de archivos a fusionar',
    example: [41750, 41751],
    type: [Number],
  })
  @IsArray()
  @ArrayMinSize(1)
  @IsNumber({}, { each: true })
  fileIds!: number[];
}

/**
 * DTO para error de archivo PDF en merge
 */
export class ControlDusPdfMergeFileErrorDto {
  @ApiProperty({
    description: 'Nombre del archivo con error',
    example: 'documento.pdf',
  })
  fileName!: string;

  @ApiProperty({
    description: 'Tipo de error',
    enum: ['ENCRYPTED_PDF', 'CORRUPTED_PDF', 'READ_ERROR', 'UNKNOWN_ERROR'],
    example: 'ENCRYPTED_PDF',
  })
  errorType!: string;

  @ApiProperty({
    description: 'Descripción del error',
    example: 'El PDF está protegido con contraseña',
  })
  errorDescription!: string;

  @ApiPropertyOptional({
    description: 'Ruta del archivo',
    example: '/path/to/file.pdf',
  })
  filePath?: string;

  @ApiPropertyOptional({
    description: 'Tamaño del archivo',
    example: 57513,
  })
  fileSize?: number;
}

/**
 * DTO para respuesta de error en merge de PDFs
 */
export class ControlDusPdfMergeErrorResponseDto {
  @ApiProperty({
    description: 'Éxito de la operación',
    example: false,
  })
  success!: boolean;

  @ApiProperty({
    description: 'Mensaje de error',
    example: 'Error al procesar 1 archivo(s) PDF',
  })
  message!: string;

  @ApiProperty({
    description: 'Código de error',
    example: 'PDF_PROCESSING_ERROR',
  })
  errorCode!: string;

  @ApiProperty({
    description: 'Lista de errores por archivo',
    type: [ControlDusPdfMergeFileErrorDto],
  })
  fileErrors!: ControlDusPdfMergeFileErrorDto[];

  @ApiProperty({
    description: 'Número de instrucción de embarque',
    example: 6216,
  })
  nroIE!: number;

  @ApiProperty({
    description: 'Timestamp del error',
    example: '2025-10-25T20:00:00.000Z',
  })
  timestamp!: string;
}
